/**
 * Throwable equipment: two slots, two rosters.
 *
 * ## Where the shape came from
 *
 * The *structure* is the modern-shooter standard, and it is worth stating plainly
 * what was taken and what was not. Taken: that a loadout carries exactly one lethal
 * and one tactical; that both rosters unlock across the whole career rather than
 * front-loading; that the lethal roster is a set of variations on "when does it go
 * off" (timed, on contact, on command, on proximity) plus area denial, while the
 * tactical roster is a set of ways to take something away from the enemy (sight,
 * mobility, information) or give something to yourself.
 *
 * Not taken, deliberately and per the project's standing rule: no names, no
 * descriptions, no numbers. Every identifier, display name, blurb, unlock level,
 * fuse, radius and damage figure below is authored for this game and tuned against
 * this game's own time-to-kill and map scale. A 7 m blast radius here is a decision
 * about DUST YARD's 104 m of ground and its 55 m longest sightline, not a figure
 * copied from somewhere it would mean something else.
 *
 * ## Why these twelve
 *
 * One entry per *mechanic*, and nothing that is only a number. A roster where two
 * items differ by half a metre of radius is a roster where eleven of the twelve are
 * decoration. Each of these takes a different action away from the player who is hit
 * by it, or gives a different action to the player who threw it:
 *
 *   lethal    when it detonates, and what the blast leaves behind
 *   tactical  which of sight / movement / information it removes
 *
 * ## The unlock curve
 *
 * Both rosters start at level 1 with a workhorse and finish in the high forties, so
 * equipment keeps arriving for as long as weapons do. The two ladders interleave
 * rather than align: a level that grants a lethal does not also grant a tactical, so
 * the player gets a new thing to try roughly twice as often as either list alone
 * would give them.
 */

/** Fuse behaviour. What ends the projectile's flight and starts its effect. */
export const TRIGGER = {
  /** Counts down from the moment it leaves the hand. Can be cooked. */
  TIMED: 'timed',
  /** Detonates on the first thing it touches. */
  IMPACT: 'impact',
  /** Sticks where it lands, then counts down. */
  STICKY: 'sticky',
  /** Sticks and waits for the thrower to press the button again. */
  REMOTE: 'remote',
  /** Sticks, arms after a delay, then fires when someone comes close. */
  PROXIMITY: 'proximity',
};

/** What happens when the trigger fires. Each is a branch in `Equipment._detonate`. */
export const EFFECT = {
  BLAST: 'blast', // radial damage
  BURN: 'burn', // a pool of fire that damages over time
  CONCUSS: 'concuss', // slows movement and aim
  FLASH: 'flash', // blinds, and only through clear line of sight
  SMOKE: 'smoke', // a volume that actually blocks sight, including the AI's
  SENSOR: 'sensor', // paints enemies on the minimap for a while
  STIM: 'stim', // heals the thrower
  SHOCK: 'shock', // holds an enemy still and stops them firing
};

/**
 * The lethal roster.
 *
 * `damage` is at the centre and falls to zero at `radius`, so the figures read as
 * "what this does to someone standing on it". 100 health means the frag is a kill
 * inside about two metres and a serious wound out to five — deliberately not a
 * whole-room kill, because a lethal that clears a corridor is a lethal everyone
 * carries and nobody thinks about.
 */
export const LETHAL = [
  {
    id: 'frag_m2',
    name: 'M2 フラググレネード',
    nameEn: 'M2 FRAG',
    unlock: 1,
    trigger: TRIGGER.TIMED,
    effect: EFFECT.BLAST,
    fuse: 3.4,
    /** Held before the throw to burn fuse. The classic risk: hold it too long. */
    cookable: true,
    damage: 165,
    radius: 6.4,
    throwSpeed: 17,
    bounce: 0.34,
    blurb: 'ピンを抜いたまま持てる。投げる前に何秒クックするかが全て。',
    blurbEn: 'Cookable. How long you hold it is the whole decision.',
  },
  {
    id: 'cling_44',
    name: 'クリング44 粘着弾',
    nameEn: 'CLING-44 ADHESIVE',
    unlock: 8,
    trigger: TRIGGER.STICKY,
    effect: EFFECT.BLAST,
    fuse: 2.0,
    damage: 175,
    radius: 5.2,
    throwSpeed: 19,
    bounce: 0,
    blurb: '当たった場所に貼りつく。転がって戻ってこない。',
    blurbEn: 'Sticks where it lands. Never rolls back at you.',
  },
  {
    id: 'rapstrike',
    name: 'ラップストライク 着発弾',
    nameEn: 'RAPSTRIKE IMPACT',
    unlock: 15,
    trigger: TRIGGER.IMPACT,
    effect: EFFECT.BLAST,
    fuse: 0,
    damage: 140,
    radius: 4.6,
    throwSpeed: 24,
    bounce: 0,
    blurb: '触れた瞬間に起爆。当てる必要がある代わりに待ち時間はゼロ。',
    blurbEn: 'Goes off the instant it touches anything. No wait, no second chance.',
  },
  {
    id: 'emberline',
    name: 'エンバーライン 焼夷弾',
    unlock: 22,
    nameEn: 'EMBERLINE INCENDIARY',
    trigger: TRIGGER.IMPACT,
    effect: EFFECT.BURN,
    fuse: 0,
    damage: 26, // per second, inside the pool
    radius: 3.6,
    duration: 7.5,
    throwSpeed: 20,
    bounce: 0,
    blurb: 'キルではなくエリア封鎖用。ルートを一本潰す。',
    blurbEn: 'Not for killing. For making a doorway cost something.',
  },
  {
    id: 'rc4_charge',
    name: 'RC-4 遠隔炸薬',
    nameEn: 'RC-4 REMOTE CHARGE',
    unlock: 30,
    trigger: TRIGGER.REMOTE,
    effect: EFFECT.BLAST,
    fuse: 0,
    damage: 195,
    radius: 7.0,
    throwSpeed: 14,
    bounce: 0,
    blurb: '設置して待ち、任意のタイミングで起爆。トリガーは自分。',
    blurbEn: 'Place it, wait, and choose the moment. You are the fuse.',
  },
  {
    id: 'thornwire',
    name: 'ソーンワイヤー 感知地雷',
    nameEn: 'THORNWIRE PROXIMITY MINE',
    unlock: 40,
    trigger: TRIGGER.PROXIMITY,
    effect: EFFECT.BLAST,
    fuse: 0,
    /** Seconds after landing before it will trigger. Stops instant self-kills. */
    armTime: 1.1,
    /** Someone inside this starts the (short) trigger delay. */
    trip: 3.2,
    triggerDelay: 0.35,
    damage: 180,
    radius: 5.6,
    throwSpeed: 13,
    bounce: 0,
    blurb: '背後を任せる装備。自分が通らないルートに置く。',
    blurbEn: 'A door you do not have to watch. Put it where you are not going.',
  },
];

/**
 * The tactical roster.
 *
 * Every one of these removes something specific: sight, mobility, the ability to
 * fire, or the enemy's belief that they are unobserved. `STIM` is the exception and
 * is the one that gives rather than takes, which is why it is the only tactical with
 * no effect on anyone else at all.
 */
export const TACTICAL = [
  {
    id: 'jolt_c',
    name: 'ジョルト 衝撃弾',
    nameEn: 'JOLT CONCUSSION',
    unlock: 1,
    trigger: TRIGGER.TIMED,
    effect: EFFECT.CONCUSS,
    fuse: 1.6,
    radius: 7.5,
    duration: 3.2,
    /** Movement multiplier at the centre. Aim rate is slowed by the same figure. */
    strength: 0.42,
    throwSpeed: 18,
    bounce: 0.4,
    blurb: '視界は残したまま移動と射撃を鈍らせる。追撃しやすい状態で足止め。',
    blurbEn: 'Leaves them their eyes and takes their legs. You can still follow up.',
  },
  {
    id: 'nova_f',
    name: 'ノヴァ 閃光弾',
    nameEn: 'NOVA FLASH',
    unlock: 6,
    trigger: TRIGGER.TIMED,
    effect: EFFECT.FLASH,
    fuse: 1.4,
    radius: 11,
    duration: 3.6,
    throwSpeed: 18,
    bounce: 0.4,
    /** Needs clear line of sight — a wall between is total protection. */
    needsSight: true,
    blurb: '壁越しには一切効かない。見えていた相手だけがブラインドになる。',
    blurbEn: 'Nothing through a wall. Only what could see it stops seeing.',
  },
  {
    id: 'veil_s',
    name: 'ヴェール 発煙弾',
    nameEn: 'VEIL SMOKE',
    unlock: 14,
    trigger: TRIGGER.IMPACT,
    effect: EFFECT.SMOKE,
    fuse: 0,
    radius: 6.5,
    duration: 12,
    throwSpeed: 18,
    bounce: 0.2,
    blurb: '実際に視線を遮断する。ボットの視界にも同じように効く。',
    blurbEn: 'Actually blocks line of sight — the AI is blinded by it too.',
  },
  {
    id: 'tellmark',
    name: 'テルマーク 探知ダート',
    nameEn: 'TELLMARK SENSOR DART',
    unlock: 20,
    trigger: TRIGGER.IMPACT,
    effect: EFFECT.SENSOR,
    fuse: 0,
    radius: 24,
    duration: 14,
    throwSpeed: 26,
    bounce: 0,
    blurb: '刺した場所の周りを、しばらくのあいだ見えるようにする。',
    blurbEn: 'Everything near where it lands shows on your map for a while.',
  },
  {
    id: 'mendkit',
    name: 'メンドキット 応急注射',
    nameEn: 'MENDKIT STIM',
    unlock: 28,
    trigger: TRIGGER.IMPACT,
    effect: EFFECT.STIM,
    fuse: 0,
    /** Applied to the thrower, immediately, wherever they are looking. */
    heal: 70,
    /** Also clears the regeneration delay, which is most of the value. */
    throwSpeed: 0,
    bounce: 0,
    blurb: '相手には何もしない。撃ち合いの途中でヘルスを作り直す装備。',
    blurbEn: 'Takes nothing from anyone. Buys you the health to trade back.',
  },
  {
    id: 'arcpin',
    name: 'アークピン 電撃杭',
    nameEn: 'ARCPIN SHOCK STAKE',
    unlock: 36,
    trigger: TRIGGER.PROXIMITY,
    effect: EFFECT.SHOCK,
    fuse: 0,
    armTime: 0.9,
    trip: 3.4,
    triggerDelay: 0.1,
    radius: 4.2,
    duration: 2.4,
    /** Movement multiplier while held, and the weapon is inhibited outright. */
    strength: 0.15,
    throwSpeed: 15,
    bounce: 0,
    blurb: '踏んだ相手はその場で停止し、射撃もできなくなる。キルは自分で取る。',
    blurbEn: 'Whoever trips it stops moving and stops shooting. Killing them is your job.',
  },
];

export const EQUIPMENT = {};
for (const e of [...LETHAL, ...TACTICAL]) EQUIPMENT[e.id] = e;

/** What a fresh career starts with — the two level-1 entries. */
export const DEFAULT_LOADOUT = { lethal: 'frag_m2', tactical: 'jolt_c' };

/** Everything of one kind, in unlock order. */
export function rosterFor(slot) {
  return slot === 'tactical' ? TACTICAL : LETHAL;
}

/** True when a career level has earned this piece. */
export function isUnlocked(id, level) {
  const e = EQUIPMENT[id];
  return !!e && level >= e.unlock;
}

/** The unlock levels at which anything at all arrives, ascending. */
export function unlockLevels() {
  return [...new Set([...LETHAL, ...TACTICAL].map((e) => e.unlock))].sort((a, b) => a - b);
}
